import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import PropTypes from 'prop-types';
import { useMountedState } from 'react-use';
import classnames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import omit from 'lodash/omit';
import transform from 'lodash/transform';

import { apiClient } from '../../../services/api';
import Error, { ErrorTypes } from '../../error/Error';

import PhotoUploadInput from './photo-upload-input/PhotoUploadInput';
import PhotoThumbnail, { PhotoStatus } from './photo-thumbnail/PhotoThumbnail';

import './PhotoUpload.scss';

enum ActionType {
  ADD = 'add',
  REMOVE = 'remove',
  LOADING = 'loading',
  RENEW = 'renew',
  ERROR = 'error',
  SUCCESS = 'success',
}

const actionStatuses = {
  [ActionType.LOADING]: PhotoStatus.LOADING,
  [ActionType.ERROR]: PhotoStatus.ERROR,
  [ActionType.SUCCESS]: PhotoStatus.SUCCESS,
};

const UPLOAD_FILES_LIMIT = 10;

const getErrorlessState = (state) =>
  transform(
    state,
    (acc, file, id) => {
      if (!file.error) {
        acc[id] = file;
      }

      return acc;
    },
    {},
  );

export interface Photo {
  id?: string;
  file?;
  objectUrl?;
  status?: PhotoStatus;
  error?;
}

type PhotosState = Record<string, Photo>;

export const newPhotosHandler = (photos: PhotosState, dispatch) => {
  const newPhotos = Object.values(photos).filter(
    ({ status }) => status === PhotoStatus.NEW,
  );

  newPhotos.forEach(({ file, id }) => {
    dispatch({ type: ActionType.LOADING, id });
    apiClient
      .uploadPhoto(file)
      .then((response) => {
        const fileId = response.result && response.result.fileId;

        if (fileId) {
          dispatch({ type: ActionType.SUCCESS, id, fileId });
        } else {
          dispatch({
            type: ActionType.ERROR,
            id,
            error: 'This photo could not be uploaded.',
          });
        }
      })
      .catch((error) => {
        dispatch({
          type: ActionType.ERROR,
          id,
          error: error.message,
        });
      });
  });
};

export const initialLoadingPhotosHandler = (photos: PhotosState, dispatch) => {
  Object.values(photos)
    .filter(({ status }) => status === PhotoStatus.LOADING)
    .forEach(({ id }) => dispatch({ type: ActionType.RENEW, id }));
};

export const photosReducer = (state: PhotosState = {}, action): PhotosState => {
  switch (action.type) {
    case ActionType.ADD: {
      const newPhotos = {};
      action.files.forEach((file) => {
        const objectUrl = URL.createObjectURL(file);
        const id = uniqueId();
        newPhotos[id] = {
          id,
          file,
          objectUrl,
          status: PhotoStatus.NEW,
          error: null,
        };
      });

      state = getErrorlessState(state);
      return { ...state, ...newPhotos };
    }
    case ActionType.REMOVE: {
      return getErrorlessState(omit(state, action.id));
    }
    case ActionType.RENEW: {
      const photo = state[action.id];

      if (!photo) {
        return state;
      }

      return {
        ...state,
        [action.id]: {
          ...photo,
          status: PhotoStatus.NEW,
        },
      };
    }
    case ActionType.LOADING:
    case ActionType.ERROR:
    case ActionType.SUCCESS: {
      let photo = state[action.id];

      if (!photo) {
        if (action.type === ActionType.ERROR) {
          photo = {};
        } else {
          return state;
        }
      }

      if (action.type === ActionType.ERROR) {
        state = getErrorlessState(state);
      }

      return {
        ...state,
        [action.id]: {
          ...photo,
          status: actionStatuses[action.type],
          fileId: action.type === ActionType.SUCCESS ? action.fileId : null,
          error: action.type === ActionType.ERROR ? action.error : null,
        },
      };
    }
  }

  return state;
};

export interface PhotoUploadProps {
  className?: string;
  value: PhotosState;
  children?: ReactNode;
  disabled?: boolean;
  onChange?;
  photosDispatch?;
}

const PhotoUpload = ({
  className,
  value: outerPhotos,
  children,
  disabled,
  onChange,
  photosDispatch: outerDispatch,
}: PhotoUploadProps) => {
  const [innerPhotos, setInnerPhotos] = useState<PhotosState>({});
  const photos = useMemo(
    () =>
      // in case state is shared between components/forms and controlled outside
      outerDispatch ? outerPhotos : innerPhotos,
    [outerDispatch, innerPhotos, outerPhotos],
  );

  const photosRef = useRef<PhotosState>();
  // keep track for async dispatch
  photosRef.current = photos;

  const isMounted = useMountedState();

  const photosDispatch = useCallback(
    (action) => {
      if (outerDispatch) {
        return outerDispatch(action);
      }

      const newState = photosReducer(photosRef.current, action);
      // save new state in case of sequential actions so it will be accumulated until updated from inner or outer state
      photosRef.current = newState;

      if (isMounted()) {
        // async effects may occur after component unmounted, so change should be called, but no state update
        setInnerPhotos(newState);
      }

      if (onChange) {
        onChange(newState);
      }

      return action;
    },
    [isMounted, outerDispatch, onChange],
  );

  const handleRemove = useCallback(
    (id) => {
      photosDispatch({ type: ActionType.REMOVE, id });
    },
    [photosDispatch],
  );

  const handlePhotos = useCallback(
    (files) => photosDispatch({ type: ActionType.ADD, files }),
    [photosDispatch],
  );

  const handleError = useCallback(
    (error) =>
      // track error as a file to share the state
      photosDispatch({ type: ActionType.ERROR, id: uniqueId(), error }),
    [photosDispatch],
  );

  // Upload added photos
  useEffect(() => {
    if (!outerDispatch) {
      newPhotosHandler(photos, photosDispatch);
    }
  }, [photos, photosDispatch, outerDispatch]);

  const isMaxPhotosUploaded = useMemo(
    () =>
      Object.values(photos).filter(
        ({ status }) => (status as string) === ActionType.SUCCESS,
      ).length >= UPLOAD_FILES_LIMIT,
    [photos],
  );

  const photosList = useMemo(() => Object.values(photos), [photos]);
  const errorlessPhotos = useMemo(
    () => photosList.filter(({ error }) => !error),
    [photosList],
  );
  const errorPhoto = useMemo(
    () => photosList.find(({ error }) => error),
    [photosList],
  );

  return (
    <div className={classnames('photo-upload', className)}>
      <PhotoUploadInput
        multiple={true}
        disabled={disabled || isMaxPhotosUploaded}
        onFiles={handlePhotos}
        onError={handleError}
      />
      {isMaxPhotosUploaded && (
        <Error
          className="photo-upload__hint"
          text="Up to 10 files can be uploaded."
          type={ErrorTypes.warning}
        />
      )}
      {errorPhoto && (
        <Error className="photo-upload__error" text={errorPhoto.error} />
      )}
      {errorlessPhotos.length > 0 && (
        <div className="photo-upload__preview-grid">
          {errorlessPhotos.map(({ id, objectUrl, status, error }) => (
            <PhotoThumbnail
              className="photo-upload__preview-item"
              key={id}
              status={status as string}
              error={error}
              src={objectUrl}
              onRemove={() => handleRemove(id)}
            />
          ))}
        </div>
      )}
      {children}
    </div>
  );
};

PhotoUpload.propTypes = {
  onChange: PropTypes.func.isRequired,
};

export default PhotoUpload;
